/**
 * `ctx.charts` — the OS charting surface.
 *
 * Everything here is deliberately narrow. There is one factory, `create`, and
 * the chart type is a string on the options object rather than a family of
 * methods, so adding `candlestick` later is a registry entry (see
 * `registerSeriesType` in ./series) and not a new public method.
 *
 * The palette and colour helpers are exposed too, because an app that draws a
 * legend of its own, or a status pill, must be able to reach the SAME hues —
 * otherwise the rules hold inside the chart and break the moment anything is
 * drawn beside it.
 */

import type { ChartsApi } from "@arclog/module-sdk";
import { onThemeApplied } from "../theme-host";
import { Chart } from "./chart";
import {
  categoricalColor,
  categoricalPalette,
  divergingRamp,
  sequentialRamp,
  statusColor,
} from "./charts-palette.mjs";
import { modeForSurface } from "./charts-color.mjs";
import { createStatTile } from "./stat-tile";
import { registerSeriesType } from "./series";
import { resolveChartTheme, resolveMode, resolveSurface } from "./theme";
import type { ChartHandle, ChartMode, ChartOptions, ChartTheme } from "./types";

export function createChartsApi() {
  const live = new Set<ChartHandle>();

  /**
   * Every chart subscribes to this. `onThemeApplied` fires after the shell has
   * swapped the CSS variables, which is the only moment `--os-window-body` is
   * guaranteed to hold the new value.
   */
  const subscribeTheme = (fn: () => void) => onThemeApplied(fn);

  function create(options: ChartOptions): ChartHandle {
    const kind = options.kind || "line";
    const handle =
      kind === "statTile"
        ? createStatTile(options, subscribeTheme)
        : new Chart(options, subscribeTheme);
    live.add(handle);
    const destroy = handle.destroy.bind(handle);
    handle.destroy = () => {
      live.delete(handle);
      destroy();
    };
    return handle;
  }

  return {
    create,
    /** Register a custom series renderer (this is how candlestick/volume land). */
    registerSeriesType,
    /**
     * Resolve the palette mode an element would get right now — useful when an
     * app draws its own chrome next to a chart and must match.
     */
    mode: (el?: HTMLElement | null): ChartMode => resolveMode(el || null, "auto"),
    surface: (el?: HTMLElement | null): string => resolveSurface(el || null),
    theme: (el?: HTMLElement | null): ChartTheme => resolveChartTheme(el || null, "auto"),
    /** The fixed categorical ramp. Order is the contract; it is never cycled. */
    palette: (mode?: ChartMode) => categoricalPalette(mode || "dark"),
    /** Colour for a slot. Throws outside 0..7 rather than wrapping. */
    color: (slot: number, mode?: ChartMode) => categoricalColor(slot, mode || "dark"),
    /** Reserved status hue. Requires a label — status is never colour alone. */
    status: statusColor,
    /** One hue, light → dark. */
    sequential: sequentialRamp,
    /** Two hues + neutral grey midpoint. Odd step counts only. */
    diverging: divergingRamp,
    /** Which palette a surface colour wants. */
    modeForSurface,
    /** Subscribe to theme changes (charts do this themselves). */
    onThemeChange: subscribeTheme,
    // `satisfies` (not a return annotation) so the SDK contract is
    // compiler-checked while internal callers keep the precise inferred type —
    // same pattern as `createOsUiApi`.
  } satisfies ChartsApi;
}

export type OsChartsApi = ReturnType<typeof createChartsApi>;
export type { ChartHandle, ChartOptions, ChartTheme, ChartMode };
